import { supabase }                 from './supabase.js';
import { initLogin }                from './auth/login.js';
import { renderOverview }           from './views/overview.js';
import { renderAttendance }         from './views/attendance.js';
import { renderLearners }           from './views/learners.js';
import { renderProgrammes }         from './views/programmes.js';
import { renderFacilitators }       from './views/facilitators.js';
import { renderClasses }            from './views/classes.js';
import { renderPayslips }           from './views/payslips.js';
import { renderHrEmployees }        from './views/hr-employees.js';
import { renderHrPayslips }         from './views/hr-payslips.js';
import { renderAdminHome }          from './views/admin-home.js';
import { renderAdminPeople }        from './views/admin-people.js';
import { renderAdminAudit }         from './views/admin-audit.js';
import { renderReports }            from './views/reports.js';
import { loadAccess, isAdminView }  from './lib/access.js';
import { renderCompliance }         from './views/compliance.js';
import { renderAlerts }            from './views/alerts.js';
import { openChangePasswordModal }  from './components/change-password.js';
import { mountChatWidget }          from './components/chat-widget.js';

const boot      = document.getElementById('boot-screen');
const login     = document.getElementById('login-screen');
const app       = document.getElementById('app');
const role      = document.getElementById('topbar-role');
const avatar    = document.getElementById('topbar-avatar');
const logoutBtn = document.getElementById('logout-btn');

const VALID_VIEWS = ['overview','attendance','learners','programmes','facilitators','classes','learner-payslips','hr-employees','hr-payslips','admin','admin-people','admin-audit','reports','compliance','alerts'];
// The old Users view lives on as People & rights; Learner documents is now a tab of Payslips & documents.
const VIEW_ALIASES = { users: 'admin-people', 'admin-learner-docs': 'learner-payslips' };
let navWired    = false;
let _appShowing = false;

function show(el) {
  for (const e of [boot, login, app]) e.classList.add('hidden');
  el.classList.remove('hidden');
}

function viewFromHash() {
  const raw = (location.hash || '').replace(/^#\/?/, '');
  if (raw === 'admin-learner-docs') window.__payslipsTab = 'settings';
  const h = VIEW_ALIASES[raw] ?? raw;
  return VALID_VIEWS.includes(h) ? h : 'overview';
}

function showView(name) {
  if (!VALID_VIEWS.includes(name)) name = 'overview';

  if (location.hash !== `#/${name}`) {
    history.replaceState(null, '', `#/${name}`);
  }

  document.querySelectorAll('.nav-item').forEach(n =>
    n.classList.toggle('active', n.dataset.view === name)
  );
  document.querySelectorAll('.view').forEach(v =>
    v.classList.toggle('hidden', v.id !== `view-${name}`)
  );
  setMode(name);

  const renderers = {
    overview:     renderOverview,
    attendance:   renderAttendance,
    learners:     renderLearners,
    programmes:   renderProgrammes,
    facilitators: renderFacilitators,
    classes:      renderClasses,
    'learner-payslips': renderPayslips,
    'hr-employees': renderHrEmployees,
    'hr-payslips':  renderHrPayslips,
    admin:          renderAdminHome,
    'admin-people': renderAdminPeople,
    'admin-audit':  renderAdminAudit,
    reports:      renderReports,
    compliance:   renderCompliance,
    alerts:       renderAlerts,
  };

  if (renderers[name]) {
    renderers[name]().catch(err => {
      document.getElementById(`view-${name}`).innerHTML =
        `<div class="page-header"><h1>Error</h1><p>${err.message}</p></div>`;
      console.error(err);
    });
  }
}

function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

/**
 * Which side of the rail shows (docs/admin-area.md). Links, not state: the
 * side follows the page you are on, so a reload or a shared link lands on
 * the right rail. Without the Admin role there is only the Work rail.
 */
function setMode(viewName) {
  const access = window.__access;
  const adminMode = !!access?.admin && isAdminView(viewName);
  document.getElementById('nav-work').classList.toggle('hidden', adminMode);
  document.getElementById('nav-admin').classList.toggle('hidden', !adminMode);
  document.getElementById('mode-work').classList.toggle('active', !adminMode);
  document.getElementById('mode-admin').classList.toggle('active', adminMode);
  document.getElementById('mode-work').setAttribute('aria-selected', String(!adminMode));
  document.getElementById('mode-admin').setAttribute('aria-selected', String(adminMode));
  const chip = document.getElementById('topbar-role');
  if (chip && access) chip.textContent = `${adminMode ? 'Admin · ' : ''}${cap(access.role)} · ${window.__userEmail ?? ''}`;
}

async function bootstrap() {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    _appShowing = false;
    initLogin();
    show(login);
    const pending = sessionStorage.getItem('login_pending_msg');
    if (pending) {
      sessionStorage.removeItem('login_pending_msg');
      const msgEl = document.getElementById('login-msg');
      if (msgEl) { msgEl.textContent = pending; msgEl.className = 'login-msg error'; }
    }
    return;
  }

  const email = session.user.email || '';
  const access = await loadAccess();
  const r = access?.role ?? null;

  if (!r) {
    sessionStorage.setItem(
      'login_pending_msg',
      `${email} is signed in but has no role assigned yet. Contact the administrator to be granted access.`
    );
    await supabase.auth.signOut();
    return;
  }

  _appShowing = true;
  show(app);
  avatar.textContent = (email[0] || '?').toUpperCase();
  role.textContent = `${cap(r)} · ${email}`;
  window.__userRole  = r;        // made available to all view modules
  window.__userEmail = email;
  window.__access    = access;   // role + the parts switched on for this person (lib/access.js)

  // An admin gets the Work | Admin toggle, and HR lives on the Admin side.
  // Anyone else with the Staff HR part sees HR on the Work rail.
  document.getElementById('mode-switch').classList.toggle('hidden', !access.admin);
  document.querySelectorAll('.nav-hr').forEach(li => li.classList.toggle('hidden', !(access.hr && !access.admin)));
  document.querySelectorAll('.nav-hr-admin').forEach(li => li.classList.toggle('hidden', !access.hr));
  document.querySelectorAll('.nav-learner-pay-admin').forEach(li => li.classList.toggle('hidden', !access.learnerPay));
  // Learner pay (payslips + documents): same split as HR — Admin side for an admin,
  // the Work rail for anyone else with the part; facilitators see no pay.
  document.querySelectorAll('.nav-learner-pay').forEach(li => li.classList.toggle('hidden', !(access.learnerPay && !access.admin)));
  // Reports (management): same split again.
  document.querySelectorAll('.nav-reports-admin').forEach(li => li.classList.toggle('hidden', !access.reports));
  document.querySelectorAll('.nav-reports').forEach(li => li.classList.toggle('hidden', !(access.reports && !access.admin)));

  // Admin-only: mount chat widget (testing phase)
  if (access.admin) mountChatWidget(supabase);

  if (!navWired) {
    navWired = true;
    const sidebar = document.getElementById('sidebar');
    document.querySelectorAll('.nav-item').forEach(n => {
      n.addEventListener('click', (e) => {
        if (n.classList.contains('nav-item--coming-soon')) { e.preventDefault(); return; }
        if (!n.dataset.view) return; // real link (e.g. Reports Guide) — let it navigate normally
        e.preventDefault();
        showView(n.dataset.view);
      });
    });
    // The admin home's cards are hash links; the hashchange listener below routes them.
    document.querySelectorAll('.mode-btn').forEach(b => b.addEventListener('click', () => sidebar.classList.remove('open')));
    logoutBtn.addEventListener('click', async () => {
      await supabase.auth.signOut();
    });
    document.getElementById('change-pw-btn').addEventListener('click', openChangePasswordModal);
    window.addEventListener('hashchange', () => showView(viewFromHash()));

    // Mobile: hamburger toggle + auto-close on nav click + tap-outside close
    const toggle  = document.getElementById('sidebar-toggle');
    toggle.addEventListener('click', () => sidebar.classList.toggle('open'));
    document.querySelectorAll('.nav-item').forEach(n => {
      n.addEventListener('click', () => sidebar.classList.remove('open'));
    });
    document.addEventListener('click', (e) => {
      if (window.innerWidth > 768) return;
      if (!sidebar.contains(e.target) && !toggle.contains(e.target)) {
        sidebar.classList.remove('open');
      }
    });
  }

  showView(viewFromHash());
}

supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    if (_appShowing) {
      sessionStorage.setItem('login_pending_msg', 'Your session expired. Please sign in again.');
    }
    _appShowing = false;
    bootstrap();
  } else if (event === 'SIGNED_IN' && !_appShowing) {
    bootstrap();
  }
  // TOKEN_REFRESHED, USER_UPDATED, INITIAL_SESSION → no-op; view stays intact
});

bootstrap();
